import { Stack, TableCell, Tooltip } from '@mui/material';
import React from 'react';
import { generatePath } from 'react-router';

import { ProcessListItemDto } from '@/api/generated';
import {
  ExpandableNameCell,
  ExpandableTableRow,
} from '@/components/table/expandable/ExpandableTableRow.comp.tsx';
import { TableRowLink } from '@/components/table/TableRowLink.comp.tsx';
import { SmartDate } from '@/components/ui/smartDate/SmartDate.comp.tsx';
import { StatusPill } from '@/components/ui/statusPill/StatusPill.comp.tsx';
import { processStatusTone } from '@/components/ui/statusPill/statusTone.util.ts';
import { UserAvatar } from '@/components/ui/userAvatar/UserAvatar.comp.tsx';
import { personFromFullName } from '@/components/ui/userAvatar/userAvatar.util.ts';
import {
  inquirySourceAppearance,
  inquirySourceLabels,
} from '@/models/inquirySource/InquirySource.model.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useAppNavigation } from '@/utils/useAppNavigation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { ProcessesListRowDetails } from '@/views/processes/list/components/table/components/ProcessesListRowDetails.comp.tsx';

type Props = {
  onCustomAction?: (row: ProcessListItemDto) => Promise<void> | void;
  row: ProcessListItemDto;
};

const InquirySourceIcon: React.FC<{ source?: ProcessListItemDto['inquirySource'] }> = ({
  source,
}) => {
  const { t } = useTranslationWithPrefix('views.statistics');
  const { t: tDetails } = useTranslationWithPrefix('views.processes.table.details');
  if (!source) {
    return null;
  }
  const key = inquirySourceLabels[source];
  const { accent, Icon } = inquirySourceAppearance[key];
  return (
    <Tooltip title={`${tDetails('inquirySource')}: ${t(`sourceLabels.${key}`)}`}>
      <Icon sx={{ color: accent, flexShrink: 0, fontSize: 15 }} />
    </Tooltip>
  );
};

export const ProcessesListTableRow: React.FC<Props> = ({ onCustomAction, row }) => {
  const { t } = useTranslationWithPrefix('views.processes.table.body');
  const { push } = useAppNavigation();

  const detailsPath = generatePath(RouteEnum.PROCESSES_DETAILS, {
    processId: row.processId.toString(),
  });

  return (
    <ExpandableTableRow
      details={<ProcessesListRowDetails detailsPath={detailsPath} processId={row.processId} />}
      onRowClick={onCustomAction ? () => onCustomAction(row) : () => push(detailsPath)}
    >
      <ExpandableNameCell>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
          <InquirySourceIcon source={row.inquirySource} />
          {onCustomAction ? row.name : <TableRowLink to={detailsPath}>{row.name}</TableRowLink>}
        </Stack>
      </ExpandableNameCell>
      <TableCell>{row.customer}</TableCell>
      <TableCell>
        <UserAvatar person={personFromFullName(row.owner)} role={t('owner')} />
      </TableCell>
      <TableCell>
        <UserAvatar person={personFromFullName(row.creator)} role={t('creator')} />
      </TableCell>
      <TableCell>
        <StatusPill
          label={t(`processStatusOptions.${row.status}`)}
          tone={processStatusTone[row.status]}
        />
      </TableCell>
      <TableCell>
        <SmartDate date={row.createdAt} />
      </TableCell>
    </ExpandableTableRow>
  );
};
